import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Box,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { themeColors } from '../common/constants';
import { exportSurveyData } from '../../utils/exportUtils';

// Office and sub-office data
const offices = [
  { label: 'All Offices', value: 'all' },
  { label: 'Office of the Regional Director', value: 'Office of the Regional Director' },
  { label: 'Administrative Division', value: 'Administrative Division' },
  { label: 'Policy Planning and Research Division', value: 'Policy Planning and Research Division' },
  { label: 'Field Technical Assistance Division', value: 'Field Technical Assistance Division' },
  { label: 'Quality Assurance Division', value: 'Quality Assurance Division' },
  { label: 'Curriculum and Learning Management Division', value: 'Curriculum and Learning Management Division' },
  { label: 'Human Resource and Development Division', value: 'Human Resource and Development Division' },
  { label: 'Education Support Services Division', value: 'Education Support Services Division' },
  { label: 'Finance Division', value: 'Finance Division' }
];

// Sub-offices
const subOffices = {
  'Office of the Regional Director': [
    { label: 'All Sub-Offices', value: 'all' },
    { label: 'Office of the Regional Director', value: 'Office of the Regional Director' },
    { label: 'Office of the Assistant Regional Director', value: 'Office of the Assistant Regional Director' },
    { label: 'Information Communication and Technology Unit', value: 'Information Communication and Technology Unit' },
    { label: 'Legal Unit', value: 'Legal Unit' },
    { label: 'Public Affairs Unit', value: 'Public Affairs Unit' }
  ],
  'Administrative Division': [
    { label: 'All Sub-Offices', value: 'all' },
    { label: 'Admin Proper', value: 'Admin Proper' },
    { label: 'Asset Management Section', value: 'Asset Management Section' },
    { label: 'Cash Section', value: 'Cash Section' },
    { label: 'General Services Unit', value: 'General Services Unit' },
    { label: 'Personnel Section', value: 'Personnel Section' },
    { label: 'Procurement Unit', value: 'Procurement Unit' },
    { label: 'Records Section', value: 'Records Section' },
    { label: 'Payroll Services Unit', value: 'Payroll Services Unit' }
  ],
  'Human Resource and Development Division': [
    { label: 'All Sub-Offices', value: 'all' },
    { label: 'Human Resource and Development Division', value: 'Human Resource and Development Division' },
    { label: 'NEAP - National Educators\' Academy of the Philippines', value: 'NEAP - National Educators\' Academy of the Philippines' }
  ]
};

// Helper function to format date for API and display
const formatDateForApi = (date) => {
  if (!date) return null;
  
  // Make sure we have a Date object
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) return null;
  
  // Format as YYYY-MM-DD for API
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const FilterDialog = ({ 
  open, 
  onClose, 
  onApplyFilters, 
  showSnackbar,
  currentFilters = {},
  feedbackCount
}) => {
  // Initialize state with current filters if available
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [office, setOffice] = useState('all');
  const [subOffice, setSubOffice] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [availableSubOffices, setAvailableSubOffices] = useState([]);
  const [hasSubOffices, setHasSubOffices] = useState(false);
  
  // Update form values when dialog opens or currentFilters change
  useEffect(() => {
    if (open) {
      try {
        // Parse date strings into Date objects
        setStartDate(currentFilters.start_date ? new Date(currentFilters.start_date) : null);
        setEndDate(currentFilters.end_date ? new Date(currentFilters.end_date) : null);
        setOffice(currentFilters.office || 'all');
        setSubOffice(currentFilters.sub_office || 'all');
      } catch (error) {
        console.error('Error parsing dates:', error);
        setStartDate(null);
        setEndDate(null);
      }
    }
  }, [open, currentFilters]);
  
  // Update available sub-offices when office changes
  useEffect(() => {
    if (office !== 'all' && subOffices[office]) {
      setAvailableSubOffices(subOffices[office]);
      setHasSubOffices(true);
    } else {
      setAvailableSubOffices([]);
      setSubOffice('all');
      setHasSubOffices(false);
    }
  }, [office]);
  
  // Handle clearing all filters
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setOffice('all');
    setSubOffice('all');
  };
  
  // Handle exporting filtered data
  const handleExportFiltered = async () => {
    // Validate date range if provided
    if ((startDate && !endDate) || (!startDate && endDate)) {
      showSnackbar('Please select both start and end dates for filtering', 'error');
      return;
    }
    
    // Format dates for API
    const formattedStartDate = formatDateForApi(startDate);
    const formattedEndDate = formatDateForApi(endDate);
    
    // Create custom date range object
    const customDateRange = (formattedStartDate && formattedEndDate) ? {
      startDate: formattedStartDate, 
      endDate: formattedEndDate
    } : null;
    
    // Create filters object
    const filters = {
      office: office !== 'all' ? office : null,
      sub_office: (hasSubOffices && subOffice !== 'all') ? subOffice : null
    };
    
    setExporting(true);
    try {
      // Export data with filters
      const result = await exportSurveyData('custom', customDateRange, handleExportError);
      if (result.success) {
        handleExportSuccess('Data exported successfully');
      }
    } finally {
      setExporting(false);
    }
  };
  
  // Apply filters to the data view
  const handleApplyFilters = () => {
    // Validate date range if provided
    if ((startDate && !endDate) || (!startDate && endDate)) {
      showSnackbar('Please select both start and end dates for filtering', 'error');
      return;
    }
    
    // Format dates for API
    const formattedStartDate = formatDateForApi(startDate);
    const formattedEndDate = formatDateForApi(endDate);
    
    // Create filters object
    const filters = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      office: office !== 'all' ? office : null,
      sub_office: (hasSubOffices && subOffice !== 'all') ? subOffice : null
    };
    
    // Remove null values for cleaner object
    Object.keys(filters).forEach(key => 
      filters[key] === null && delete filters[key]
    );
    
    // Pass filters up to parent component
    if (onApplyFilters) {
      onApplyFilters(filters);
    }
    
    onClose();
  };
  
  // Success and error handlers
  const handleExportSuccess = (message) => {
    if (showSnackbar) {
      showSnackbar(message, 'success');
    } else {
      console.log(message);
    }
  };
  
  const handleExportError = (message) => {
    if (showSnackbar) {
      showSnackbar(message, 'error');
    } else {
      console.error(message);
    }
  };

  // Count active filters for button label
  const getActiveFilterCount = () => {
    let count = 0;
    if (startDate) count++;
    if (endDate) count++;
    if (office !== 'all') count++;
    if (hasSubOffices && subOffice !== 'all') count++;
    return count;
  };
  
  const activeFilterCount = getActiveFilterCount();
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: themeColors.primary, color: 'white' }}>
        Filter Options
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Date Range
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  maxDate={endDate || undefined}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate || undefined}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Office Location
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Office/Division</InputLabel>
                <Select
                  value={office}
                  onChange={(e) => setOffice(e.target.value)}
                  label="Office/Division"
                >
                  {offices.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {hasSubOffices && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Sub-Office/Section</InputLabel>
                  <Select
                    value={subOffice}
                    onChange={(e) => setSubOffice(e.target.value)}
                    label="Sub-Office/Section"
                  >
                    {availableSubOffices.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          <Button 
            onClick={handleClearFilters}
            sx={{ mr: 1 }}
          >
            Clear All
          </Button>
         
        </Box>
        
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApplyFilters}
            startIcon={<FilterAltIcon />}
            sx={{ 
              bgcolor: themeColors.primary,
              '&:hover': {
                bgcolor: themeColors.primaryDark
              }
            }}
          >
            Apply Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FilterDialog;